/**
 * Credentials-file helpers used by `serve.ts` for Claude Code subscription
 * credentials. Extracted so other modules (e.g. AI 401 mediation) can reuse
 * the atomic rewrite without importing from the much larger `serve.ts`.
 *
 * @category Credentials
 * @since 3.3.0
 */

import { existsSync, readFileSync } from "node:fs";
import { mkdir, readFile, rename, writeFile } from "node:fs/promises";
import { join } from "node:path";

/**
 * Well-known platform key for the Claude Code OAuth credentials JSON blob
 * forwarded by `provision_secrets`. The runner writes it to
 * `<home>/.claude/.credentials.json` instead of the secret provider since
 * the SDK reads it as a file.
 *
 * @docLink packages/runner/dev-guide#environment-variables
 */
export const CLAUDE_CODE_CREDENTIALS_KEY = "CLAUDE_CODE_CREDENTIALS_JSON";

/**
 * Writes the Claude Code credentials JSON blob to the Claude user config dir
 * at the current canonical Linux path, `~/.claude/.credentials.json`, with
 * mode `0600`. Creates the parent directory with mode `0700` if needed.
 *
 * Atomic via tmp-file + rename within the same directory.
 *
 * Exported for unit tests + the AI 401 mediation helper.
 *
 * @docLink packages/runner/dev-guide#environment-variables
 */
export async function writeClaudeCodeCredentialsFile(home: string, json: string): Promise<string> {
  const claudeDir = join(home, ".claude");
  await mkdir(claudeDir, { recursive: true, mode: 0o700 });
  const finalPath = join(claudeDir, ".credentials.json");
  const tmpPath = `${finalPath}.tmp.${process.pid}.${Date.now()}.${Math.random().toString(36).slice(2)}`;
  await writeFile(tmpPath, json, { mode: 0o600 });
  await rename(tmpPath, finalPath); // POSIX atomic on same fs
  return finalPath;
}

/**
 * Ensure `<home>/.claude/settings.json` carries `disableClaudeAiConnectors: true`.
 *
 * With a claude.ai subscription credential on disk, Claude Code fetches every
 * connector of the seat owner's claude.ai account and advertises it to the
 * session as `mcp__claude_ai_<Name>__*` tools. The key is honored from any
 * settings scope with any-source-true semantics, so writing it into the user
 * scope switches the fetch off regardless of project settings or SDK options.
 *
 * Claude Code honors the key from Agent SDK 0.3.258 on; older CLIs ignore it
 * (their settings schema passes unknown keys through), which is why the driver
 * also sets `ENABLE_CLAUDEAI_MCP_SERVERS=false` — that flag every CLI honors.
 *
 * Writes `<home>/.claude/settings.json`, the same directory the credentials
 * file goes to. That is the CLI's user scope only while `CLAUDE_CONFIG_DIR` is
 * unset or equal to it — an image that moves the config dir must move both
 * writes, or the CLI never reads this file and the success log lies.
 *
 * Read-merge-write: every other key of an existing file survives (in
 * production `~/.claude` is a persistent per-session bind mount, never a
 * scratch dir). A missing or unparseable file starts from `{}` and is
 * replaced; genuine fs errors (EACCES, EROFS, …) propagate so the caller
 * decides. Idempotent — already-`true` files are left untouched. Atomic via
 * tmp-file + rename like {@link writeClaudeCodeCredentialsFile}.
 *
 * @returns The path to the settings file.
 */
export async function ensureClaudeSettingsDisablesConnectors(home: string): Promise<string> {
  const claudeDir = join(home, ".claude");
  await mkdir(claudeDir, { recursive: true, mode: 0o700 });
  const finalPath = join(claudeDir, "settings.json");
  let settings: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(await readFile(finalPath, "utf-8"));
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      settings = parsed as Record<string, unknown>;
    }
  } catch (err) {
    // Absent or corrupt → start from {}; anything else is a real fs error.
    const isMissing = (err as NodeJS.ErrnoException).code === "ENOENT";
    if (!isMissing && !(err instanceof SyntaxError)) throw err;
  }
  if (settings.disableClaudeAiConnectors === true) return finalPath;
  settings.disableClaudeAiConnectors = true;
  const tmpPath = `${finalPath}.tmp.${process.pid}.${Date.now()}.${Math.random().toString(36).slice(2)}`;
  await writeFile(tmpPath, `${JSON.stringify(settings, null, 2)}\n`, { mode: 0o600 });
  await rename(tmpPath, finalPath); // POSIX atomic on same fs
  return finalPath;
}

/**
 * Atomically merge a fresh `accessToken` into the credentials JSON at
 * `<home>/.claude/.credentials.json`, preserving any other top-level fields
 * and the nested `claudeAiOauth` shape the platform writes at provision time.
 *
 * Read-modify-write under {@link writeClaudeCodeCredentialsFile}'s atomic-
 * rename semantics. Used by the runner's `onAuthError` callback (in
 * `serve.ts`) when the bridge driver asks for a credential refresh after
 * a 401 from the underlying agent.
 *
 * @param home - The home directory under which `.claude/.credentials.json`
 *   lives.
 * @param accessToken - The fresh access token to persist.
 * @param expiresAt - Optional ISO timestamp for the rotated token.
 * @returns The path to the rewritten credentials file.
 *
 * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
 * § "Runner-side handling on 401" (now bridge-side).
 */
export async function mergeAndRewriteCredentialsFile(
  home: string,
  accessToken: string,
  expiresAt?: string | null,
): Promise<string> {
  const credPath = join(home, ".claude", ".credentials.json");
  let merged: Record<string, unknown> = {};
  if (existsSync(credPath)) {
    try {
      merged = JSON.parse(readFileSync(credPath, "utf-8")) as Record<string, unknown>;
    } catch {
      merged = {};
    }
  }
  const oauthBlock = ((merged as { claudeAiOauth?: Record<string, unknown> }).claudeAiOauth ??
    {}) as Record<string, unknown>;
  oauthBlock.accessToken = accessToken;
  if (expiresAt) oauthBlock.expiresAt = expiresAt;
  (merged as { claudeAiOauth?: Record<string, unknown> }).claudeAiOauth = oauthBlock;
  // Top-level accessToken too, for SDK builds that read the flat shape.
  (merged as { accessToken?: string }).accessToken = accessToken;
  return writeClaudeCodeCredentialsFile(home, JSON.stringify(merged));
}

/**
 * Extract the access-token expiry from a raw Claude Code credentials JSON
 * blob. Supports both the nested `claudeAiOauth.expiresAt` shape the
 * platform writes at provision time and an optional top-level `expiresAt`
 * field used by some SDK builds.
 *
 * Returns `null` when the field is missing, empty, or not a parseable
 * date — callers treat `null` as "static token, don't schedule a
 * proactive refresh". Tolerates both ISO-8601 strings and millisecond
 * epochs (some Claude Code builds emit the epoch; normalise to ISO so
 * downstream code has one format to reason about).
 *
 * Used by the runner's proactive AI-credential refresh scheduler in
 * `serve.ts` to compute the next refresh fire time after a fresh
 * credential lands (initial provision OR `host.refresh_credential`
 * response). The runner never mutates the credentials blob; this is a
 * pure read.
 *
 * @docLink packages/runner/dev-guide#ai-credential-mediation
 */
export function extractClaudeAiOauthExpiresAt(json: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as Record<string, unknown>;
  const oauth = obj.claudeAiOauth as Record<string, unknown> | undefined;
  const raw = oauth?.expiresAt ?? obj.expiresAt;
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = Date.parse(raw);
    return Number.isFinite(t) ? new Date(t).toISOString() : null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    // Epoch milliseconds (Claude Code subscription tokens encode it this way).
    return new Date(raw).toISOString();
  }
  return null;
}

/**
 * Read the current access token from the on-disk credentials file at
 * `<home>/.claude/.credentials.json`. Supports both the nested
 * `claudeAiOauth.accessToken` shape the platform writes and the flat
 * top-level `accessToken` some SDK builds use.
 *
 * Used by the 401-mediation path to fingerprint the just-rejected token (the
 * one currently on disk) before asking the platform to rotate it, so the
 * platform can coalesce concurrent rotations of the same dead token. Returns
 * `null` when the file is absent, unreadable, or carries no token.
 *
 * @docLink packages/runner/dev-guide#ai-credential-mediation
 */
export function readCurrentClaudeAccessToken(home: string): string | null {
  const credPath = join(home, ".claude", ".credentials.json");
  if (!existsSync(credPath)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(credPath, "utf-8"));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as { claudeAiOauth?: { accessToken?: unknown }; accessToken?: unknown };
  const raw = obj.claudeAiOauth?.accessToken ?? obj.accessToken;
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}
